import React, { useState, useEffect, useCallback } from 'react';
import { useVideoEvents, VideoFilterOptions } from '@/hooks/useVideoEvents';
import { getNextDate, getPreviousDate } from './utils/calendarHelpers';
import CalendarHeader from './CalendarHeader';
import WeekView from './views/WeekView';
import MonthView from './views/MonthView';
import ListView from './views/ListView';
import LoadingState from './views/LoadingState';
import SearchInput from './components/filter/SearchInput';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionLock } from '@/components/subscription/SubscriptionLock';

interface WeeklyCalendarViewProps {
  selectedProfiles: string[];
}

const WeeklyCalendarView: React.FC<WeeklyCalendarViewProps> = ({ selectedProfiles }) => {
  // States
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeViewType, setActiveViewType] = useState<'week' | 'month' | 'list'>('week');
  const [filterOptions, setFilterOptions] = useState<VideoFilterOptions>({
    title: '',
    startDate: undefined,
    endDate: undefined,
    statuses: [],
    contentTypes: [],
    showNotes: false
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  
  // Custom hooks
  const { videoEvents, isLoading } = useVideoEvents(selectedProfiles, filterOptions);
  const { isSubscriptionActive, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  
  // Navigation and handlers
  const goToNext = useCallback(() => {
    setCurrentDate(getNextDate(currentDate, activeViewType));
  }, [currentDate, activeViewType]);
  
  const goToPrevious = useCallback(() => {
    setCurrentDate(getPreviousDate(currentDate, activeViewType));
  }, [currentDate, activeViewType]);
  
  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);
  
  const handleViewChange = useCallback((viewType: 'week' | 'month' | 'list') => {
    setActiveViewType(viewType);
  }, []);

  const handleFilterChange = useCallback((newFilters: VideoFilterOptions) => {
    console.log("Applying new filters:", newFilters);
    setFilterOptions(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    console.log("Clearing all filters");
    setFilterOptions({
      title: '',
      startDate: undefined,
      endDate: undefined,
      statuses: [],
      contentTypes: [],
      showNotes: false
    });
  }, []);
  
  const handleVideoClick = useCallback((videoId?: string) => {
    console.log("Video clicked:", videoId);
  }, []);

  // Count active filters
  useEffect(() => {
    let count = 0;
    if (filterOptions.title && filterOptions.title.trim() !== '') count++;
    if (filterOptions.startDate) count++;
    if (filterOptions.endDate) count++;
    if (filterOptions.statuses && filterOptions.statuses.length > 0) count++;
    if (filterOptions.contentTypes && filterOptions.contentTypes.length > 0) count++;
    if (filterOptions.showNotes) count++;
    setActiveFiltersCount(count);
  }, [filterOptions]);
  
  // Determine if we should show calendar content or lock screen
  // This avoids the flash by checking subscription status before rendering content
  const shouldShowCalendarContent = !isSubscriptionLoading && isSubscriptionActive;
  const shouldShowLockScreen = !isSubscriptionLoading && !isSubscriptionActive;
  
  // Render calendar content
  const renderCalendarContent = useCallback(() => {
    if (isLoading || isSubscriptionLoading) {
      return <LoadingState />;
    }

    // If subscription check is complete and not active, show lock screen
    if (shouldShowLockScreen) {
      return (
        <SubscriptionLock 
          title="Calendrier verrouillé"
          description="L'accès au calendrier détaillé n'est disponible qu'avec un abonnement actif."
          buttonText="Débloquer le calendrier"
        />
      );
    }

    // Only render actual calendar if subscription is active and loading is complete
    if (shouldShowCalendarContent) {
      switch (activeViewType) {
        case 'month':
          return (
            <MonthView 
              currentDate={currentDate} 
              videoEvents={videoEvents || []} 
              onVideoClick={handleVideoClick} 
            />
          );
        case 'list':
          return (
            <ListView 
              videoEvents={videoEvents || []} 
              onVideoClick={handleVideoClick} 
              filterOptions={filterOptions}
            />
          );
        case 'week':
        default:
          return (
            <WeekView 
              currentDate={currentDate} 
              videoEvents={videoEvents || []} 
              onVideoClick={handleVideoClick} 
            />
          );
      }
    }
    
    // Default loading state as fallback
    return <LoadingState />;
  }, [
    activeViewType, 
    currentDate, 
    filterOptions, 
    handleVideoClick, 
    isLoading, 
    isSubscriptionLoading,
    shouldShowCalendarContent,
    shouldShowLockScreen,
    videoEvents
  ]);
  
  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#1a1d2d] dark:text-gray-200">
      {/* Header with navigation and filters - always show this */}
      <CalendarHeader 
        currentDate={currentDate}
        activeViewType={activeViewType}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onToday={goToToday}
        onViewChange={handleViewChange}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        activeFiltersCount={activeFiltersCount}
      />

      {/* Search bar - always visible even when locked */}
      <div className="p-2 border-b border-gray-200 bg-gray-50 dark:bg-[#121624] dark:border-gray-800/50">
        <div className="flex items-center justify-between gap-2">
          <SearchInput filterOptions={filterOptions} onFilterChange={handleFilterChange} />
          
          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters} className="h-9 dark:text-gray-300 dark:hover:bg-gray-800/60">
              <X className="h-4 w-4 mr-1" />
              Effacer les filtres
            </Button>
          )}
        </div>
      </div>

      {/* Calendar content - render content based on subscription status */}
      <div className="flex-1 overflow-auto dark:bg-[#121624]">
        {renderCalendarContent()}
      </div>
    </div>
  );
};

export default WeeklyCalendarView;
